/** What the API tells a setup guide (`setup` on the agent and connections endpoints). */
export interface AgentSetup {
  server: string;
  apiBase: string;
  tokenEndpoint: string;
  /** GitHub repository that hosts the plugins and their marketplaces. */
  marketplace: string;
  instructions: string;
}

export type AgentSetupMode =
  { kind: 'personal' } | { kind: 'service_account'; agentName: string; clientId?: string | undefined };

/** The server the plugin uses when none is given. */
export const DEFAULT_SERVER = 'https://jira.lixionary.com';

/** Apps a person can connect from. Cursor, Antigravity and Codex share the plugins/lixionary-jira-agent plugin. */
export type AgentClient = 'claude-code' | 'cursor' | 'antigravity' | 'codex';
export const AGENT_CLIENTS: { value: AgentClient; label: string }[] = [
  { value: 'claude-code', label: 'Claude Code' },
  { value: 'cursor', label: 'Cursor' },
  { value: 'antigravity', label: 'Antigravity' },
  { value: 'codex', label: 'Codex' },
];

const AGENT_PLUGIN = '~/.lixionary-jira/plugins/lixionary-jira-agent';

/**
 * Copyable setup for the lixionary-jira plugins. Personal agents install the plugin for their app and sign in
 * through the browser; service accounts run the plugin's CLI with client credentials from the environment.
 * Secrets are never embedded.
 */
export function agentSetupCommands(
  setup: AgentSetup,
  mode: AgentSetupMode,
  client: AgentClient = 'claude-code',
): string {
  const server = setup.server === DEFAULT_SERVER ? '' : ` ${setup.server}`;
  if (mode.kind === 'personal' && client === 'claude-code')
    return [
      `/plugin marketplace add ${setup.marketplace}`,
      '/plugin install lixionary-jira@lixionary',
      `/lixionary-jira:login${server}`,
    ].join('\n');
  if (mode.kind === 'personal') {
    const fetchPlugin = [
      `git clone --depth 1 --filter=blob:none --sparse https://github.com/${setup.marketplace} ~/.lixionary-jira`,
      `git -C ~/.lixionary-jira sparse-checkout set plugins/lixionary-jira-agent${client === 'codex' ? ' .agents/plugins' : ''}`,
    ];
    const install =
      client === 'cursor'
        ? [
            'mkdir -p ~/.cursor/plugins/local',
            `ln -sfn ${AGENT_PLUGIN} ~/.cursor/plugins/local/lixionary-jira`,
            '# Reload the Cursor window, then in the agent chat:',
          ]
        : client === 'codex'
          ? [
              'codex plugin marketplace add ~/.lixionary-jira',
              'codex plugin add lixionary-jira@lixionary',
              '# Restart Codex and start a new chat, then send:',
            ]
          : [
              `agy plugin install ${AGENT_PLUGIN}`,
              `# For the Antigravity IDE instead: mkdir -p ~/.gemini/config/plugins && ln -sfn ${AGENT_PLUGIN} ~/.gemini/config/plugins/lixionary-jira`,
              '# Then in the agent chat:',
            ];
    const login = client === 'codex' ? '$lixionary-jira-login' : '/lixionary-jira-login';
    return [...fetchPlugin, ...install, `${login}${server}`].join('\n');
  }
  return [
    `# Service account "${mode.agentName}". Keep the secret in your CI secret store; never print, log or commit it.`,
    `export LIXIONARY_JIRA_URL=${setup.server}`,
    `export LIXIONARY_JIRA_CLIENT_ID=${mode.clientId ?? '<client ID>'}`,
    'export LIXIONARY_JIRA_CLIENT_SECRET=<client secret>',
    'lixionary-jira status   # or: node plugins/lixionary-jira/lib/lixionary-jira.cjs status',
  ].join('\n');
}
